import { useMemo } from 'react';
import { camelCase, compact, uniqueId } from 'lodash';

import { IconImage24 } from 'src/assets/icons/24x24/IconImage24';
import IconChrome from 'src/assets/icons/IconChrome';
import { IconCopilot } from 'src/assets/icons/IconCopilot';
import {
  BrandVoiceIcon,
  IconWTM,
  IntegrationsResourcesIcon,
  Performance,
  SavedTemplatesIcon,
} from 'src/assets/icons/navbar-links';
import { EN_LANGUAGES } from 'src/data/generation';
import { INFINITY_EXTENSION_STORE_URL } from 'src/data/infinity';
import { ROUTES } from 'src/routes';
import { useCustomerImageGenerationSupported } from 'src/services/customer/useCustomerImageGenerationSupported';
import { useCustomerPerformanceChartEnabled } from 'src/services/customer/useCustomerPerformanceChartEnabled';
import { useBrandVoices } from 'src/services/useBrandVoices';
import { useVisualLanguages } from 'src/services/useVisualLanguages';
import { ANALYTICS_PAGE_VIEW } from 'src/views/CopyAnalyticsPage';

import { ITEM_TYPE } from '../lib';

const COPILOT_AGENT_MARKETPLACE_URL =
  'https://marketplace.microsoft.com/en-us/product/WA200011143?tab=Overview';

const createLinkItem = ({ label, path, icon, isChildItem, externalLink, emptySubmenuItem }) => ({
  type: ITEM_TYPE.link,
  id: `${camelCase(label)}LinkItem`,
  label,
  path,
  icon,
  isChildItem,
  externalLink,
  options: {
    emptySubmenuItem,
  },
});

const createHeaderItem = ({ label }) => ({
  type: ITEM_TYPE.header,
  id: uniqueId('header'),
  label,
});

const createMenuItem = ({ label, icon, items, canCreateItemType, isChildItem }) => ({
  type: ITEM_TYPE.menu,
  id: `${camelCase(label)}MenuItem`,
  label,
  icon,
  items,
  isChildItem,
  canCreateItemType,
});

const createBrandVoiceMenuItem = ({ brandVoice, items }) => ({
  id: `${camelCase(brandVoice.name)}${brandVoice.id}CollapsableItem`,
  label: brandVoice.name,
  type: ITEM_TYPE.brandVoice,
  items,
  brandVoice,
});

const createVisualLanguageItem = ({ visualLanguage, items }) => ({
  id: `${camelCase(visualLanguage.name)}${visualLanguage.id}CollapsableItem`,
  label: visualLanguage.name,
  type: ITEM_TYPE.visualLanguage,
  items,
  visualLanguage,
});

const createEmptyItem = ({ label }) => createLinkItem({ label, emptySubmenuItem: true });

export const useNavigationSidebarItems = () => {
  const { brandVoices } = useBrandVoices();
  const { workspaceVisualLanguages } = useVisualLanguages();
  const performanceChartEnabled = useCustomerPerformanceChartEnabled();
  const imageGenerationSupported = useCustomerImageGenerationSupported();

  return useMemo(() => {
    const sidebarItems = [
      createHeaderItem({ label: 'Assets & Tools' }),

      createMenuItem({
        label: 'Content Intelligence',
        icon: Performance,
        items: compact([
          createLinkItem({
            label: performanceChartEnabled ? 'Data rows' : 'Analytics',
            path: ROUTES.ANALYTICS,
            isChildItem: true,
          }),
          performanceChartEnabled &&
            createLinkItem({
              label: 'Analytics',
              path: `${ROUTES.ANALYTICS}/${ANALYTICS_PAGE_VIEW.chart}`,
              isChildItem: true,
            }),
          createLinkItem({
            label: 'A/B tested talking points',
            path: ROUTES.TALKING_POINTS,
            isChildItem: true,
          }),
        ]),
      }),

      createMenuItem({
        label: 'Brand Voice',
        canCreateItemType: ITEM_TYPE.brandVoice,
        icon: BrandVoiceIcon,
        items:
          brandVoices?.length > 0
            ? brandVoices.map((brandVoice) => {
                const isEnglishOrUndefined =
                  EN_LANGUAGES.includes(brandVoice.language) || !brandVoice.language;

                const items = [
                  createLinkItem({
                    label: 'Tone of Voice',
                    path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.TONE_OF_VOICE}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Target Audiences',
                    path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.PERSONAS}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Messaging Bank',
                    path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.BRAND_BRIEFS}`,
                    isChildItem: true,
                  }),
                  createLinkItem({
                    label: 'Brand Vocabulary',
                    path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.BRAND_TERMS}`,
                    isChildItem: true,
                  }),
                ];

                if (isEnglishOrUndefined) {
                  items.unshift(
                    createLinkItem({
                      label: 'Custom Models',
                      path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.CUSTOM_MODELS}`,
                      isChildItem: true,
                    })
                  );
                  items.push(
                    createLinkItem({
                      label: 'Text Formats',
                      path: `${ROUTES.BRAND_VOICE}/${brandVoice.id}${ROUTES.FORMAT}`,
                      isChildItem: true,
                    })
                  );
                }

                return createBrandVoiceMenuItem({
                  brandVoice,
                  items,
                });
              })
            : [createEmptyItem({ label: 'No brand voices are accessible' })],
      }),

      imageGenerationSupported &&
        createMenuItem({
          label: 'Visual Language',
          canCreateItemType: ITEM_TYPE.visualLanguage,
          icon: IconImage24,
          items:
            workspaceVisualLanguages?.length > 0
              ? workspaceVisualLanguages.map((visualLanguage) => {
                  const items = [
                    createLinkItem({
                      label: 'Image Styles',
                      path: `${ROUTES.VISUAL_LANGUAGE}/${visualLanguage.id}${ROUTES.IMAGE_STYLE}`,
                      isChildItem: true,
                    }),
                  ];

                  return createVisualLanguageItem({
                    visualLanguage,
                    items,
                  });
                })
              : [createEmptyItem({ label: 'No visual languages' })],
        }),

      createLinkItem({
        label: 'Template Builder',
        path: ROUTES.TEMPLATE_BUILDER,
        icon: SavedTemplatesIcon,
      }),

      createLinkItem({
        label: 'Integrations & Resources',
        path: ROUTES.INTEGRATIONS_RESOURCES,
        icon: IntegrationsResourcesIcon,
      }),

      createLinkItem({
        label: 'Website Agent',
        path: ROUTES.WEBSITE_AUTOMATION,
        icon: IconWTM,
      }),

      createLinkItem({
        label: 'Get Chrome Extension',
        path: INFINITY_EXTENSION_STORE_URL,
        icon: IconChrome,
        externalLink: true,
      }),

      createLinkItem({
        label: 'Get Copilot Agent',
        path: COPILOT_AGENT_MARKETPLACE_URL,
        icon: IconCopilot,
        externalLink: true,
      }),
    ];

    return compact(sidebarItems);
  }, [performanceChartEnabled, brandVoices, imageGenerationSupported, workspaceVisualLanguages]);
};
